<template>
  <div class="flex flex-col items-center">
    <div class="flex w-80 justify-between pt-4 pb-2">
      <p class="text-body-m-bold text-main">{{ t('Reviews.product.edit.pricing.preview.title') }}</p>
      <ChipBadge class="uppercase" rounded>{{ lang }}</ChipBadge>
    </div>
    <div class="w-80" :class="pricingTier.isRecommended ? 'pt-8' : ''">
      <PricingTierCard
        :pricing-tier="pricingTier"
        :currency-symbol="priceCurrencySymbol"
        :price-locale="lang === 'de' ? 'de-DE' : 'en-US'"
        pricing-position="top"
      />
    </div>
  </div>
</template>
<script setup lang="ts">
import { ChipBadge } from '@ramp106/omrjs-core-ui'
import { PricingTierCard, type PricingTier } from '@ramp106/omrjs-hygraph-ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { type Currency } from '@/gql/reviews'
import { ALLOWED_LOCALES } from '@/helpers/translationsHelper'

import { translatedOptionName } from './translatedOptionName'
import type { TranslatedOption } from './types'
import type { PricingPlanFormData } from '../types'

const props = defineProps<{
  lang: string
  previewName: string
  pricing: PricingPlanFormData
  pricingTextOptions: TranslatedOption[]
  subscriptionModes: TranslatedOption[]
  currenciesOptions: Currency[]
}>()

const { t, te } = useI18n()
const fallbackLang = ALLOWED_LOCALES[0]

// Used when the Phrase translation has not been published yet — vue-i18n
// otherwise renders the raw key, which would surface to vendors as e.g.
// "Reviews.product.edit.pricing.promotedLabel.value.recommended".
const PROMOTED_LABEL_FALLBACKS = {
  recommended: 'Recommended',
  popular: 'Popular',
  top_offer: 'Top Offer',
} as const

const planName = computed(() => props.pricing.name[props.lang] || props.pricing.name[fallbackLang] || props.previewName)

const description = computed(() => props.pricing.description[props.lang] || props.pricing.description[fallbackLang] || '')

const perks = computed<string[]>(() =>
  props.pricing.perkList.map((perk) => perk[props.lang]?.name || perk[fallbackLang]?.name || '').filter(Boolean),
)

const pricingTextValue = computed(() => {
  if (!props.pricing.pricingTextOptionId) return undefined
  const option = props.pricingTextOptions.find((o) => o.id === props.pricing.pricingTextOptionId)
  return option ? translatedOptionName(option, props.lang) : undefined
})

const billingPeriod = computed(() => {
  if (!props.pricing.pricingSubscriptionModeId) return undefined
  const option = props.subscriptionModes.find((m) => m.id === props.pricing.pricingSubscriptionModeId)
  return option ? translatedOptionName(option, props.lang) : undefined
})

const priceCurrencySymbol = computed(() => {
  if (!props.pricing.priceCurrency) return '€'
  return props.currenciesOptions.find((c) => c.code === props.pricing.priceCurrency)?.symbol ?? '€'
})

const recommendationLabel = computed(() => {
  const label = props.pricing.promotedLabel
  if (!label) return ''
  const key = `Reviews.product.edit.pricing.promotedLabel.value.${label}`
  return te(key, props.lang) ? t(key, {}, { locale: props.lang }).toString() : PROMOTED_LABEL_FALLBACKS[label]
})

// Adapter between PricingPlanFormData (eventmanager's edit shape) and
// PricingTier (the shape PricingTierCard renders). Mirrors
// reviews-frontend's planToTier so the eventmanager preview tracks the
// public PDP card 1:1.
const pricingTier = computed<PricingTier>(() => {
  const hasPrice = !pricingTextValue.value && typeof props.pricing.priceCents === 'number'
  return {
    id: props.pricing._vid,
    isRecommended: props.pricing.promoted && props.pricing.promotedLabel != null,
    pricingTierTitle: planName.value,
    pricingTierSubTitle: description.value || null,
    pricingTierTitleIcon: props.pricing.titleIcon ?? '',
    pricingTierRecommendationLabel: recommendationLabel.value,
    featuresTitle: '',
    features: perks.value.join('\n'),
    hasPrice,
    priceValue: hasPrice && props.pricing.priceCents != null ? props.pricing.priceCents / 100 : null,
    paidPer: billingPeriod.value ? `/ ${billingPeriod.value}` : null,
    pricingDescription: props.pricing.startingPrice
      ? t('Reviews.product.edit.pricing.preview.startingAt', {}, { locale: props.lang }).toString()
      : null,
    noPriceTopline: null,
    noPriceBottomline: pricingTextValue.value ?? null,
    primaryCtaLabel: null,
    primaryCtaLink: null,
    secondaryCtaLabel: null,
    secondaryCtaLink: null,
  }
})
</script>
